import { uploadS3StreamChunk } from "@/services/axios/unProtectedApi";

const DB_NAME = "mple-media-rescue";
const STORE_NAME = "chunks";
const DB_VERSION = 2;

export interface DBChunkData {
  id: string; // resultId_partNumber
  resultId: string;
  blob: Blob;
  uploadId: string;
  key: string;
  partNumber: number;
  resultType?: string;
  timestamp: number;
}

const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined" || !indexedDB) {
      return reject(
        new Error("indexedDB is not supported in this environment")
      );
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (db.objectStoreNames.contains(STORE_NAME)) {
        db.deleteObjectStore(STORE_NAME);
      }
      const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
      store.createIndex("resultId", "resultId", { unique: false });
    };

    request.onsuccess = (event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };

    request.onerror = (event) => {
      const error = (event.target as IDBOpenDBRequest).error;
      reject(new Error(error?.message || "Unknown IDB open error"));
    };
  });
};

export const saveChunkToIDB = async (
  data: Omit<DBChunkData, "timestamp" | "id">
): Promise<void> => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);

      const storeData: DBChunkData = {
        ...data,
        id: `${data.resultId}_${data.partNumber}`,
        timestamp: Date.now(),
      };

      const request = store.put(storeData);

      request.onsuccess = () => resolve();
      request.onerror = (e) => {
        const error = (e.target as IDBRequest).error;
        reject(new Error(error?.message || "Unknown IDB put error"));
      };
    });
  } catch (error) {
    console.warn(
      "[indexedDBHelper] Failed to save chunk to IDB, user may be out of storage or in private mode.",
      error
    );
  }
};

export const deleteChunkFromIDB = async (id: string): Promise<void> => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = (e) => {
        const error = (e.target as IDBRequest).error;
        reject(new Error(error?.message || "Unknown IDB delete error"));
      };
    });
  } catch (error) {
    console.warn("[indexedDBHelper] Failed to delete chunk from IDB.", error);
  }
};

export const deleteAllChunksForSession = async (
  resultId: string
): Promise<void> => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index("resultId");
      const request = index.openCursor(IDBKeyRange.only(resultId));

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest)
          .result as IDBCursorWithValue;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };

      request.onerror = (e) => {
        const error = (e.target as IDBRequest).error;
        reject(new Error(error?.message || "Unknown IDB bulk delete error"));
      };
    });
  } catch (error) {
    console.warn(
      "[indexedDBHelper] Failed to bulk delete chunks from IDB.",
      error
    );
  }
};

export const checkAndRescueIDB = async (): Promise<void> => {
  if (typeof navigator !== "undefined" && navigator.locks) {
    try {
      await navigator.locks.request(
        "mple-media-rescue",
        { mode: "exclusive", ifAvailable: true },
        async (lock) => {
          if (!lock) {
            console.warn(
              "[indexedDBHelper] Rescue operation is already running in another tab."
            );
            return;
          }
          await executeRescueLogic();
        }
      );
    } catch (e) {
      console.warn(
        "[indexedDBHelper] Web Locks error, proceeding without lock",
        e
      );
      await executeRescueLogic();
    }
  } else {
    // Fallback for browsers without Web Locks API
    await executeRescueLogic();
  }
};

const executeRescueLogic = async (): Promise<void> => {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);

    const getAllRequest = store.getAll();

    getAllRequest.onsuccess = async (event) => {
      const chunks = (event.target as IDBRequest).result as DBChunkData[];
      const now = Date.now();
      const FORTY_EIGHT_HOURS = 48 * 60 * 60 * 1000;

      for (const chunk of chunks) {
        if (now - chunk.timestamp > FORTY_EIGHT_HOURS) {
          // Eternal Orphan - silently delete
          console.warn(
            `[indexedDBHelper] Chunk ${chunk.id} is older than 48 hours. Deleting...`
          );
          await deleteChunkFromIDB(chunk.id);
          continue;
        }

        console.warn(
          `[indexedDBHelper] Found abandoned chunk ${chunk.id}. Rescuing...`
        );

        // Attempt rescue
        try {
          const formData = new FormData();
          formData.append("file", chunk.blob);
          formData.append("uploadId", chunk.uploadId);
          formData.append("key", chunk.key);
          formData.append("partNumber", chunk.partNumber.toString());
          if (chunk.resultId) {
            formData.append("resultId", chunk.resultId);
          }
          if (chunk.resultType) {
            formData.append("resultType", chunk.resultType);
          }

          await uploadS3StreamChunk(formData);
          console.warn(
            `[indexedDBHelper] Successfully rescued chunk ${chunk.id}.`
          );

          // Delete from IDB after success
          await deleteChunkFromIDB(chunk.id);
        } catch (error) {
          console.warn(
            `[indexedDBHelper] Failed to rescue chunk ${chunk.id}. S3 stream may have been finalized by cron.`,
            error
          );
          // Delete from IDB as the cron has likely already closed this session
          await deleteChunkFromIDB(chunk.id);
        }
      }
    };
  } catch (error) {
    console.warn("[indexedDBHelper] Failed to run rescue operation.", error);
  }
};
